'use client'

import {
	Bug,
	Heart,
	Lightbulb,
	Mail,
	MessageCircle,
	MessageSquarePlus,
	Send,
	X,
} from 'lucide-react'
import { type ReactNode, useId, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { track } from '@/lib/analytics'
import { cn } from '@/lib/utils'

/**
 * Floating feedback widget — the app's primary "encourage people to send info"
 * surface. Anchored bottom-right so it's visible on every screen at every
 * viewport without competing with the calculator's primary actions.
 *
 * Submits to POST /api/feedback. The tool requires nothing to use it, so the
 * email field here is explicitly optional ("contact me back" opt-in) — it does
 * not contradict the app's "no email capture" promise.
 *
 * Animation uses tw-animate-css `animate-in` utilities (already a dependency)
 * rather than framer-motion, which this app doesn't otherwise pull in.
 */

type FeedbackCategory = 'bug' | 'idea' | 'praise' | 'other'

interface CategoryOption {
	id: FeedbackCategory
	label: string
	icon: ReactNode
}

/** Closed set keeps the Plausible/stats grouping clean. */
const CATEGORIES: readonly CategoryOption[] = [
	{ id: 'idea', label: 'Idea', icon: <Lightbulb className="size-3.5" /> },
	{ id: 'bug', label: 'Bug', icon: <Bug className="size-3.5" /> },
	{ id: 'praise', label: 'Praise', icon: <Heart className="size-3.5" /> },
	{ id: 'other', label: 'Other', icon: <MessageSquarePlus className="size-3.5" /> },
]

export function FeedbackWidget() {
	const [open, setOpen] = useState(false)

	const handleToggle = () => {
		const next = !open
		setOpen(next)
		if (next) track('FeedbackOpened')
	}

	return (
		<div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-3 print:hidden">
			{open && <FeedbackPanel onClose={() => setOpen(false)} />}
			<Button
				type="button"
				size="lg"
				onClick={handleToggle}
				aria-expanded={open}
				aria-label={open ? 'Close feedback' : 'Send feedback'}
				className="rounded-full shadow-lg"
			>
				{open ? <X className="size-4" /> : <MessageCircle className="size-4" />}
				{open ? 'Close' : 'Feedback'}
			</Button>
		</div>
	)
}

/**
 * The expanded form. Owns its own field state so closing/reopening starts
 * clean. Kept as a sibling component (not inlined) so each piece stays under
 * Biome's cognitive-complexity limit.
 */
function FeedbackPanel({ onClose }: { onClose: () => void }) {
	const [category, setCategory] = useState<FeedbackCategory>('idea')
	const [message, setMessage] = useState('')
	const [email, setEmail] = useState('')
	const [submitting, setSubmitting] = useState(false)
	const messageId = useId()
	const emailId = useId()

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault()
		const trimmed = message.trim()
		if (trimmed.length === 0) {
			toast.error('Add a message first')
			return
		}

		setSubmitting(true)
		try {
			const response = await fetch('/api/feedback', {
				method: 'POST',
				headers: { 'content-type': 'application/json' },
				body: JSON.stringify({
					message: trimmed,
					email: email.trim() || undefined,
					category,
					context: {
						url: typeof window !== 'undefined' ? window.location.href : undefined,
					},
				}),
			})
			if (!response.ok) throw new Error(`HTTP ${response.status}`)
			track('FeedbackSubmitted', { category })
			toast.success('Thanks! Your feedback was sent.')
			onClose()
		} catch {
			toast.error('Could not send feedback. Please try again.')
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<form
			onSubmit={handleSubmit}
			className="w-[min(20rem,calc(100vw-2rem))] animate-in fade-in slide-in-from-bottom-2 rounded-xl border border-border bg-surface p-4 shadow-xl duration-200"
		>
			<div className="mb-3">
				<h2 className="font-heading text-base leading-tight">Send feedback</h2>
				<p className="text-[11px] text-text-muted">
					Found a bug or have an idea? We read every note.
				</p>
			</div>

			<div className="mb-3 flex flex-wrap gap-1.5">
				{CATEGORIES.map((option) => (
					<button
						key={option.id}
						type="button"
						onClick={() => setCategory(option.id)}
						aria-pressed={category === option.id}
						className={cn(
							'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs transition-colors',
							category === option.id
								? 'border-primary bg-primary/10 text-text-primary'
								: 'border-border text-text-muted hover:text-text-primary',
						)}
					>
						{option.icon}
						{option.label}
					</button>
				))}
			</div>

			<div className="mb-3 space-y-1.5">
				<Label htmlFor={messageId}>Message</Label>
				<textarea
					id={messageId}
					value={message}
					onChange={(event) => setMessage(event.target.value)}
					rows={4}
					maxLength={4000}
					placeholder="What's working, what's not, what you'd love to see…"
					className="w-full resize-none rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
				/>
			</div>

			<div className="mb-4 space-y-1.5">
				<Label htmlFor={emailId}>
					Email <span className="font-normal text-text-muted">(optional)</span>
				</Label>
				<Input
					id={emailId}
					type="email"
					value={email}
					onChange={(event) => setEmail(event.target.value)}
					placeholder="you@example.com — only if you want a reply"
				/>
			</div>

			<div className="flex items-center justify-end gap-2">
				<Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={submitting}>
					Cancel
				</Button>
				<Button type="submit" size="sm" disabled={submitting}>
					<Send className="size-3.5" />
					{submitting ? 'Sending…' : 'Send'}
				</Button>
			</div>

			{/* Direct line — opens the user's own email client pre-addressed to
			    Chris. Some people would rather just email than fill a form. */}
			<p className="mt-3 flex items-center justify-center gap-1.5 border-t border-border pt-3 text-center text-[11px] text-text-muted">
				<Mail className="size-3" />
				Prefer email?{' '}
				<a
					href="mailto:chrisleebergstrom@gmail.com?subject=Fuel%20Estimator%20Feedback"
					className="underline decoration-dotted underline-offset-2 hover:text-text-primary hover:decoration-solid"
				>
					chrisleebergstrom@gmail.com
				</a>
			</p>
		</form>
	)
}
